import { EventMeta } from '../../../interfaces/meta.interface';

type MailboxLocationDescFields = {
    mailboxId: string;
    mailboxLocationType: string;
};

const MailboxLocation: EventMeta<MailboxLocationDescFields> = {
    title: 'Mail Location Added',
    default: {
        description: 'Mail Location Added',
        longDescription: 'A Mailbox Location was added.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => ({
        mailboxId: log.mailboxId,
        mailboxLocationType: log.mailboxLocationType,
    }),
    classifications: [
        {
            severity: 'critical',
            description: (fields) => `A suspicious IP attempted to add a mailbox location`,
            longDescription: (fields) =>
                `A mailbox location was added from a suspicious IP address that is not in your allowed list. This activity could indicate an attempt to manipulate mailbox routing or access.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'danger',
            description: (fields) => `An unapproved IP attempted to add a mailbox location`,
            longDescription: (fields) =>
                `A mailbox location was added from an IP address that is not in your allowed list. This activity may indicate an attempt to manipulate mailbox routing or access.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP added a mailbox location`,
            longDescription: (fields) =>
                `A mailbox location was added. The action originated from a suspicious IP. While adding a mailbox location can be part of normal administration, the source IP being flagged as suspicious makes the event noteworthy. This is informational and does not imply immediate risk, but monitoring for repeated activity of this type may be valuable.`,
            predicates: {
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'info',
            description: (fields) =>
                `A Microsoft system operation (synchronization server IP) added a mailbox location`,
            longDescription: (fields) =>
                `A mailbox location was added. This was identified as a Microsoft system operation event, with the IP address associated with the Microsoft server responsible for synchronization. This entry is informational only and reflects standard background activity. No action is required unless unexpected changes or anomalies appear in mailbox configuration.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        {
            severity: 'info',
            description: (fields) => `A mailbox location was added`,
            longDescription: (fields) =>
                `A mailbox location was added. This is an informational record of a configuration change. No unusual conditions were noted, and no action is required unless the change to mailbox locations was unexpected.`,
        },
    ],
};

export default MailboxLocation;
